/**
 * Turns dropped files into plain text the agent can navigate.
 *
 * NOTHING NEW IN node_modules — every parser here was already installed.
 * fflate is the one now declared directly in package.json rather than
 * arriving transitively via jspdf, because this code imports it:
 *   pdfjs-dist  PDF text layer      (also used by dev/vectors/pdfextract.ts)
 *   fflate      .docx unzip         (zero-dependency; see ./docx.ts)
 *   xlsx        .xlsx/.xls          (already used by service-map import)
 *   turndown    HTML -> Markdown    (already used in several places)
 *
 * Everything runs LOCALLY. This is deliberately not the approach taken by
 * dev/vectors/pdfextract.ts, which uploads each PDF to OpenAI for extraction:
 * that costs money per document, needs an API key, and sends the user's
 * documents to a third party. A corpus is often internal policy material, so
 * it is parsed in the browser and never leaves it.
 *
 * Scanned/image-only PDFs have no text layer and will extract as empty. That
 * is reported as a skip rather than stored as a silently blank document.
 */

import * as turndownLib from "turndown"
import type { CorpusFile } from "./db"

/*
 * pdfjs, fflate and xlsx are imported LAZILY, inside the branch that needs
 * them. Two reasons: they are large (pdfjs alone pulls in a canvas-dependent
 * worker) and most sessions never upload that format, so a static import
 * would cost every user; and pdfjs touches browser globals at module scope,
 * which makes it unimportable outside a DOM.
 */

/** 25MB per file. Larger than this is almost always a mistake in a text corpus. */
const MAX_FILE_BYTES = 25 * 1024 * 1024

const TurndownService: any = (turndownLib as any).default ?? turndownLib

export type ExtractKind = CorpusFile["kind"]

/** What a given extension is treated as, and whether we can read it at all. */
const BY_EXT: Record<string, ExtractKind> = {
  md: "markdown", markdown: "markdown", mdx: "markdown",
  txt: "text", log: "text", rst: "text", adoc: "text",
  csv: "csv", tsv: "csv",
  json: "json", jsonl: "json",
  html: "html", htm: "html",
  pdf: "pdf",
  docx: "docx",
  xlsx: "xlsx", xls: "xlsx",
}

export function kindOf(path: string): ExtractKind | null {
  const ext = path.split(".").pop()?.toLowerCase() ?? ""
  return BY_EXT[ext] ?? null
}

/**
 * Files that are never worth reading even when the extension looks textual:
 * dependency trees and VCS internals bury the real documents in noise and
 * can easily be 10x the corpus by themselves.
 */
const IGNORED_SEGMENTS = new Set([
  "node_modules", ".git", ".svn", "dist", "build", ".next", ".cache",
  "__pycache__", ".venv", "venv", "vendor", ".DS_Store",
])

export function isIgnored(path: string): boolean {
  return path.split("/").some(seg => IGNORED_SEGMENTS.has(seg))
}

export interface ExtractResult {
  ok: boolean
  text?: string
  kind?: ExtractKind
  reason?: string
}

export async function extractFile(file: File, path: string): Promise<ExtractResult> {
  if (isIgnored(path)) return { ok: false, reason: "ignored path" }

  const kind = kindOf(path)
  if (!kind) return { ok: false, reason: "unsupported file type" }
  if (file.size > MAX_FILE_BYTES) {
    return { ok: false, reason: `too large (${Math.round(file.size / 1024 / 1024)}MB)` }
  }

  try {
    const text = await extractByKind(file, kind)
    const trimmed = text.trim()
    if (!trimmed) {
      return {
        ok: false,
        reason: kind === "pdf" ? "no text layer (scanned image?)" : "empty after extraction",
      }
    }
    return { ok: true, text: trimmed, kind }
  } catch (e) {
    return { ok: false, reason: e instanceof Error ? e.message : "extraction failed" }
  }
}

async function extractByKind(file: File, kind: ExtractKind): Promise<string> {
  switch (kind) {
    case "markdown":
    case "text":
    case "csv":
      return file.text()

    case "json": {
      // Re-serialised with indentation: minified JSON is one enormous line,
      // which makes grep hits useless and read-a-slice meaningless.
      const raw = await file.text()
      try {
        return JSON.stringify(JSON.parse(raw), null, 2)
      } catch {
        return raw
      }
    }

    case "html": {
      const html = await file.text()
      const td = new TurndownService({ headingStyle: "atx", codeBlockStyle: "fenced" })
      return td.turndown(html)
    }

    case "pdf":
      return extractPdfText(file)

    case "docx": {
      // Word's own XML, unzipped and read directly — see ./docx.ts for why
      // this is not mammoth. Emits markdown headings, which corpus_outline
      // navigates by.
      const { extractDocx } = await import("./docx")
      return extractDocx(await file.arrayBuffer())
    }

    case "xlsx": {
      const { read, utils } = await import("xlsx")
      const buffer = await file.arrayBuffer()
      const wb = read(buffer, { type: "array" })
      // Each sheet becomes a labelled CSV block so a grep hit can be traced
      // back to the sheet it came from.
      return wb.SheetNames
        .map(n => `## Sheet: ${n}\n\n${utils.sheet_to_csv(wb.Sheets[n])}`)
        .join("\n\n")
    }
  }
}

/**
 * Pulls the embedded text layer out of a PDF, page by page.
 *
 * The worker URL form matches dev/vectors/pdfextract.ts, which is the setup
 * already known to build correctly under Vite here.
 */
async function extractPdfText(file: File): Promise<string> {
  const pdfjsLib = await import("pdfjs-dist")
  pdfjsLib.GlobalWorkerOptions.workerSrc =
    new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString()

  const data = await file.arrayBuffer()
  const loadingTask = pdfjsLib.getDocument({ data })
  const doc = await loadingTask.promise
  const pages: string[] = []

  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i)
    const content = await page.getTextContent()
    const text = content.items
      .map((item: any) => ("str" in item ? item.str : ""))
      .join(" ")
      .replace(/\s+/g, " ")
      .trim()
    // Page markers give the agent something citable — "page 12 of intake.pdf"
    // is a far more useful answer than an unlocated quote.
    if (text) pages.push(`[page ${i}]\n${text}`)
  }

  await loadingTask.destroy()
  return pages.join("\n\n")
}
